import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import {
  useStripe,
  useElements,
  PaymentElement
} from "@stripe/react-stripe-js";
import { callBackendApiPost, alertOps } from "../lib/api";
import { useSelector, useDispatch } from 'react-redux';
import { setPaid as updatePaid } from '../redux/userSlice';
import { setPaymentIntent as updatePaymentIntent } from "../redux/stripeSlice";
import { formatUserIdentity } from "../lib/formatting";
import setPageAndDeferNavigate from "../lib/setPageAndDeferNavigate";
import logDrtEvent from '../lib/logging';

export const usePay = (next, isPay = true) => {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [modeSet, setModeSet] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const stripeSlice = useSelector((state) => state.stripe);
  const [paid, setPaid] = useState(user?.paid || false);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [isFormComplete, setIsFormComplete] = useState(false);

  const params = new URLSearchParams(window.location.search);

  const isSelfPay = useRef(isPay);

  const isUserDataAvailable = user && (user.firstName || user.lastName || user.email || user.phoneNo);

  const payOptions = isUserDataAvailable ? {
    defaultValues: {
      billingDetails: {
        address: {
          line1: user.address?.line1 || '',
          line2: user.address?.line2 || '',
          city: user.address?.city || '',
          state: user.address?.state || '',
          postal_code: user.address?.zip || '',
          country: "US",
          // TODO: change default country if needed for the future
        }
      }
    },
  } : {};

  useEffect(() => {
    if (!elements || !paymentIntent) return;
    if (modeSet) return;

    if (!paymentIntent.amount) {
      setMessage("An unexpected error occurred. Please try again, and contact support (call or text 781-650-4492) if the issue persists.");
      return;
    }

    elements.update({ mode: 'payment', amount: paymentIntent.amount, currency: paymentIntent.currency });
    if (!isPay) elements.update({ capture_method: 'manual', setup_future_usage: 'off_session' });
    setModeSet(true);
  }, [elements, paymentIntent, modeSet, isPay]);

  useEffect(() => {
    if (!isUserDataAvailable) {
      logDrtEvent(`Waiting for redux to rehydrate`,
        {
          user,
          firstName: user?.firstName,
          lastName: user?.lastName,
          email: user?.email,
          phoneNo: user?.phoneNo,
          isUserDataAvailable
        });
      return;
    }

    logDrtEvent(`User data is available, initializing payment intent...`, {
      isUserDataAvailable,
      user,
      existingPaymentIntentID: stripeSlice.paymentIntentID
    });

    if (stripeSlice.paymentIntentID && paymentIntent && isSelfPay.current === isPay) {
      logDrtEvent('Payment intent already exists, skipping initialization');
      return;
    }

    isSelfPay.current = isPay;
    if (!isPay) {
      elements.update({ capture_method: 'manual', setup_future_usage: 'off_session' });
    }
    else {
      elements.update({
        capture_method: null,
        setup_future_usage: null
      });
    }

    callBackendApiPost("/stripe/" + (isPay ? "pay-intent" : "preauth-intent"), formatUserIdentity(user))
      .then((res) => res.json())
      .then((data) => {
        setPaymentIntent(data);
        dispatch(updatePaymentIntent(data));
      })
      .catch((error) => {
        alertOps(user, "Payment intent", error, "usePay").then(() => {
          logDrtEvent(`Error fetching payment intent with error: ${error}`);
          setMessage("Failed to initialize payment. Please try again.");
        });
      });
  }, [isPay, user, dispatch, isUserDataAvailable, stripeSlice.paymentIntentID]);

  const handleElementChange = useCallback((event) => {
    setIsFormComplete(event.complete);
  }, []);

  useEffect(() => {
    if (!stripe || !paymentIntent || !paymentIntent.client_secret) {
      return;
    }

    stripe.retrievePaymentIntent(paymentIntent.client_secret).then((result) => {
      switch (result.paymentIntent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "requires_capture":
          if (!isPay) setMessage("Authorization succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe, paymentIntent, isPay]);

  useEffect(() => {
    if (!elements) return;

    const paymentElement = elements.getElement(PaymentElement);
    if (paymentElement) {
      paymentElement.on('change', handleElementChange);
    }

    return () => {
      if (paymentElement) {
        paymentElement.off('change', handleElementChange);
      }
    };
  }, [elements, handleElementChange]);

  useEffect(() => {
    if (stripe && !paid && params.get("payment_intent_client_secret")) {
      stripe.retrievePaymentIntent(params.get("payment_intent_client_secret"))
        .then((result) => {
          if (result.paymentIntent.status === "succeeded" || (!isPay && result.paymentIntent.status === "requires_capture")) {
            setPaid(true);
            dispatch(updatePaid(true));
            setPageAndDeferNavigate(dispatch, window.location.pathname, navigate);
          }
          else {
            setMessage("Payment failed. Please try again.");
          }
        });
    }
  }, [stripe, paid, params, isPay, navigate, dispatch]);

  const handlePay = async (e) => {
    if (e) e.preventDefault();

    if (!stripe || !elements || !paymentIntent || !paymentIntent.client_secret) {
      setMessage("Payment processing is not ready. Please try again.");
      return;
    }

    setIsLoading(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw submitError;
      }

      const { error, paymentIntent: confirmedPaymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: paymentIntent.client_secret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        throw error;
      }

      if (confirmedPaymentIntent.status === "succeeded" || (!isPay && confirmedPaymentIntent.status === "requires_capture")) {
        setPaid(true);
        dispatch(updatePaid(true));
        setPageAndDeferNavigate(dispatch, next, navigate);
      } else {
        setMessage("Payment was not successful. Please try again.");
      }
    } catch (error) {
      logDrtEvent(`Error during payment: ${error}`);
      if (error.type === "card_error" || error.type === "validation_error") {
        setMessage(error.message);
      } else {
        setMessage("An unexpected error occurred. Please try again, and contact support (call or text 781-650-4492) if the issue persists.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return [handlePay, message, isLoading, paid, payOptions, isFormComplete];
};